import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaQrcode, FaHome, FaUser, FaTag, FaDownload } from 'react-icons/fa';
import QRCredentialModal from '../Home/components/QRCredentialModal/QRCredentialModal';
import './PortalLayout.scss';

interface PortalLayoutProps {
    children: React.ReactNode;
}

const PortalLayout: React.FC<PortalLayoutProps> = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [client, setClient] = useState<any>(null);
    const [showQRModal, setShowQRModal] = useState(false);
    const [activeTab, setActiveTab] = useState<'home' | 'profile' | 'promotions'>('home');
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
    const [showInstallButton, setShowInstallButton] = useState(false);

    useEffect(() => {
        const clientData = localStorage.getItem('client');
        if (clientData) {
            const parsedClient = JSON.parse(clientData);
            setClient(parsedClient);
        }

        // PWA install prompt listener
        const handleBeforeInstallPrompt = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setShowInstallButton(true);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        // Check if already installed/running as PWA
        // If running as standalone PWA, hide the install button
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
            || (window.navigator as any).standalone 
            || document.referrer.includes('android-app://');
        
        if (isStandalone) {
            setShowInstallButton(false);
        } else {
            // Show button only in browser mode
            setShowInstallButton(true);
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    useEffect(() => {
        // Update active tab based on current route
        const path = location.pathname;
        if (path.includes('/Home')) {
            setActiveTab('home');
        } else if (path.includes('/Cupones')) {
            setActiveTab('promotions');
        } else if (path.includes('/Profile')) {
            setActiveTab('profile');
        }
    }, [location]);

    const handleInstallClick = async () => {
        if (!deferredPrompt) {
            // Fallback for iOS or when prompt is not available
            alert('Para instalar la app:\n\niPhone/iPad: Toca el botón "Compartir" y selecciona "Añadir a pantalla de inicio"\n\nAndroid: Abre el menú del navegador y selecciona "Instalar aplicación"');
            return;
        }

        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        
        if (outcome === 'accepted') {
            console.log('User accepted the install prompt');
            setShowInstallButton(false);
        }
        
        setDeferredPrompt(null);
    };

    const handleNavigation = (tab: 'home' | 'profile' | 'promotions') => {
        setActiveTab(tab);
        switch (tab) {
            case 'home':
                navigate('/ClientPortal/Home');
                break;
            case 'promotions':
                navigate('/ClientPortal/Cupones');
                break;
            case 'profile':
                navigate('/ClientPortal/Profile');
                break;
        }
    };

    return (
        <div className="portal-layout">
            {/* Fixed Header */}
            <div className="portal-header">
                <div className="header-content">
                    <div className="logo-section">
                        <img 
                            src={`${process.env.PUBLIC_URL}/mp_logo_02.png`} 
                            alt="Medio Punto" 
                            className="header-logo"
                        />
                    </div>
                    <div className="header-actions">
                        {/** 
                        {showInstallButton && (
                            <button 
                                className="install-btn" 
                                onClick={handleInstallClick}
                                title="Descargar app"
                            >
                                <FaDownload />
                                <span className="install-text">Descargar App</span>
                            </button>
                        )}
                            */}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="portal-content">
                {children}
            </div>

            {/* Floating QR Button */}
            <button 
                className="floating-qr-btn"
                onClick={() => setShowQRModal(true)}
                title="Mostrar credencial QR"
            >
                <FaQrcode />
            </button>

            {/* Bottom Navigation Bar */}
            <div className="bottom-navbar">
                <button 
                    className={`nav-item ${activeTab === 'promotions' ? 'active' : ''}`}
                    onClick={() => handleNavigation('promotions')}
                >
                    <FaTag />
                    <span>Cupones</span>
                </button>
                <button 
                    className={`nav-item ${activeTab === 'home' ? 'active' : ''}`}
                    onClick={() => handleNavigation('home')}
                >
                    <FaHome />
                    <span>Inicio</span>
                </button>
                <button 
                    className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
                    onClick={() => handleNavigation('profile')}
                >
                    <FaUser />
                    <span>Perfil</span>
                </button>
            </div>

            {/* QR Credential Modal */}
            <QRCredentialModal
                show={showQRModal}
                onHide={() => setShowQRModal(false)}
                email={client?.email || ''}
                clientId={client?.client_id || 0}
                clientName={client?.username || client?.email || 'Cliente'}
            />
        </div>
    );
};

export default PortalLayout;
